import { FilterBar } from "components/FilterBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import { orderStatusConstants, orderStatusMapping } from "constants/order";
import { UserRole } from "constants/userRole";
import { parseDate } from "helpers/date";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetAllPatientsQuery } from "store/queries/patient";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

const StyledPageDataTable = styled(DataTable)`
  .p-sortable-column .p-sortable-column-icon {
    display: none !important;
  }
  .p-datatable-tbody > tr > td.p-highlight {
    background-color: unset;
    color: unset;
  }
  .p-sortable-column .p-column-title {
    display: flex;
    gap: ${sizer(1)};
    align-items: center;
    justify-content: space-between;
  }
`;

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

// The estimates page shares its bar with the type, date and column filters, so its box just takes
// what's left. This one is alone on the row, so it needs a cap of its own — full width reads as a
// giant empty field.
const SearchField = styled.span`
  flex: 1;
  min-width: ${sizer(60)};
  max-width: ${sizer(160)};

  /* The magnifier is decoration — it must never take a click meant for the field. PrimeReact
     positions it absolutely, so without this it covers part of the input. */
  > i:first-of-type {
    pointer-events: none;
  }
`;

export function PatientsTable({
  showMessage = false,
}: {
  showMessage: boolean;
}) {
  const query = useQuery();
  const navigate = useNavigate();
  const [first, setFirst] = useState(0);
  const [rows] = useState(25);
  const { scope, accountIds } = useSelector(userSelector);

  // CLE-2708 — two pieces of state on purpose. `searchInput` is what's in the box, `appliedSearch`
  // is what the list is showing: nothing is fetched until the user presses Search (or Enter), so
  // typing never reloads the page. Both start from the URL so a shared/bookmarked link still works.
  const [searchInput, setSearchInput] = useState(query.get("search") || "");
  const [appliedSearch, setAppliedSearch] = useState(query.get("search") || "");

  const skip = useMemo(
    // Skip the fetch for non-super-admin users that do not have any account
    () => scope !== UserRole.SUPER_ADMIN && !accountIds?.length,
    [scope, accountIds]
  );

  const { data, isLoading, isFetching } = useGetAllPatientsQuery(
    {
      page: first,
      pageSize: rows,
      search: appliedSearch || undefined,
      ...(scope === UserRole.SUPER_ADMIN
        ? {}
        : { accountIds: accountIds || [] }),
    },
    { skip }
  );

  const parsedPatients = useMemo(() => {
    if (!data) return [];
    const { data: patients } = data;
    const parsed = patients.map((patient) => {
      return {
        patientId: patient.id,
        firstName: patient.firstName,
        lastName: patient.lastName,
        phoneNumber: patient.phoneNumber,
        accountNumber: patient?.orders[0]?.accountNumber || "",
        orderId: patient?.orders[0]?.id || "",
        accountName: patient.account.name,
        accountId: patient.account.id,
        orderStatus: orderStatusMapping[patient?.orders[0]?.status],
        orderStatusCode: patient?.orders[0]?.status,
        orderAccountId: patient?.orders[0]?.accountId,
        orderCreatedAt: parseDate(patient?.orders[0]?.createdAt, false, true),
      };
    });
    return parsed;
  }, [data]);

  // Runs only on Search / Enter / clear. Page goes back to the first one, otherwise a search made
  // from page 3 shows "No patients found." for a term that matched fewer than three pages.
  const applySearch = (term: string) => {
    const nextSearch = term.trim();
    setSearchInput(nextSearch);
    setAppliedSearch(nextSearch);
    setFirst(0);

    const searchParams = new URLSearchParams(window.location.search);
    if (nextSearch) {
      searchParams.set("search", nextSearch);
    } else {
      searchParams.delete("search");
    }
    searchParams.delete("page");
    const queryString = searchParams.toString();
    navigate(queryString ? `?${queryString}` : window.location.pathname, {
      replace: true,
    });
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  function orderIdColumn(row: any) {
    return (
      <Button
        className="p-button-link"
        data-testid="order_action_OrderTable"
        onClick={() =>
          navigate(
            row.orderStatusCode === orderStatusConstants.DRAFT
              ? `/orders/review/${row.orderId}?accountId=${row.orderAccountId}`
              : `/orders/${row.orderId}`
          )
        }
      >
        <p className="small-text">View estimate</p>
      </Button>
    );
  }

  function getPatienFirsttName(row: any) {
    return (
      <div className="sensitive" data-pii="patient-first-name">
        {row.firstName}
      </div>
    );
  }
  function getPatientLastName(row: any) {
    return (
      <div className="sensitive" data-pii="patient-last-name">
        {row.lastName}
      </div>
    );
  }

  function getPatientPhoneNumber(row: any) {
    return <div className="sensitive">{row.phoneNumber}</div>;
  }

  // Only the FIRST load swaps the whole page for a spinner. A search keeps the bar mounted and uses
  // the table's own loading overlay instead, so the term the user just typed stays on screen.
  if (isLoading) return <LoadingSpinner />;

  return (
    <div>
      {showMessage && (
        <p className="text-orange-500 mt-2 mb-3">
          Search for patients across all accounts you have access to. If you
          have access to one hospital, this works as a hospital-wide search.
        </p>
      )}
      <FilterBar className="flex flex-wrap align-items-center gap-2 p-2 mb-4">
        {/* `p-input-icon-right` only while the clear "x" is actually rendered. With the class on and
            no second icon, the magnifier matches BOTH theme rules — `i:first-of-type` (left: .75rem)
            and `i:last-of-type` (right: .75rem) — so it stretches the full width of the field and
            eats every click on the placeholder line. */}
        <SearchField
          className={`p-input-icon-left sensitive${
            searchInput ? " p-input-icon-right" : ""
          }`}
        >
          <i className="pi pi-search" />
          {/* CLE-2708 — one box, same design as the estimates page. The server decides what the
              string matches: patient name in any order, phone number (formatting ignored — the last
              four digits are enough), account number, estimate ID, hospital name and — on the
              primary insurance only — MRN / member ID or payer. */}
          <InputText
            className="w-full"
            data-testid="name_multiselect_PatientsTable"
            id="patientSearch"
            value={searchInput}
            placeholder="Search by patient name, phone number, account number, or estimate ID"
            title="Search by patient name (any order), phone number, account number, estimate ID, hospital name, or — on the primary insurance only — MRN / member ID or payer"
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch(searchInput);
            }}
          />
          {searchInput && (
            <i
              className="pi pi-times cursor-pointer"
              data-testid="clear_search_PatientsTable"
              onClick={() => applySearch("")}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch("");
              }}
              role="button"
              tabIndex={0}
              aria-label="Clear search"
            />
          )}
        </SearchField>
        <StyledButton
          data-testid="search_PatientsTable"
          icon="pi pi-search"
          className="p-button-raised p-button-sm p-button-rounded-md"
          onClick={() => applySearch(searchInput)}
        >
          Search
        </StyledButton>
      </FilterBar>
      <StyledPageDataTable
        emptyMessage="No patients found."
        stripedRows
        loading={isFetching}
        className="p-fluid"
        value={parsedPatients}
        responsiveLayout="scroll"
      >
        <Column
          field="firstName"
          header="Patient First Name"
          className="sensitive"
          headerStyle={{ width: "10%" }}
          body={(row) => getPatienFirsttName(row)}
        />
        <Column
          field="lastName"
          header="Patient Last Name"
          headerStyle={{ width: "10%" }}
          body={(row) => getPatientLastName(row)}
        />
        <Column
          field="phoneNumber"
          header="Phone Number"
          headerStyle={{ width: "10%" }}
          body={(row) => getPatientPhoneNumber(row)}
        />
        <Column
          field="accountName"
          header="Hospital Name"
          headerStyle={{ width: "15%" }}
        />
        <Column
          field="accountNumber"
          header="Account Number"
          headerStyle={{ width: "15%" }}
        />
        <Column
          field="orderStatus"
          header="Estimate Status"
          headerStyle={{ width: "15%" }}
        />
        <Column
          field="orderCreatedAt"
          header="Estimate Date Created"
          headerStyle={{ width: "10%" }}
        />
        <Column
          field="orderId"
          header="Estimate"
          headerStyle={{ width: "15%" }}
          body={(row) => orderIdColumn(row)}
        />
      </StyledPageDataTable>
      <Paginator
        first={first}
        rows={rows}
        totalRecords={data?.total}
        onPageChange={onPageChange}
      />
    </div>
  );
}
